import {
  Gift,
  Monitor,
  Trophy,
  Tv,
  Zap,
  Star,
} from 'lucide-react';
import { OfferCountdown } from './OfferCountdown';
import championsLeagueLogo from '../assets/league/Champions.svg';
import eredivisieLogo from '../assets/league/Eredivisie.png';
import championsLeaguePhoto from '../assets/sports/512rYUX2xarV.jpg';

export function WorldCupHero({ onCtaClick, showCountdown = true, ctaLabel = 'Bekijk Aanbiedingen' }) {
  return (
    <div className="relative rounded-2xl p-6 md:p-8 overflow-hidden shadow-[0_20px_50px_rgba(10,26,95,0.4)] max-w-6xl m-auto border border-yellow-500/30 bg-gradient-to-br from-[#051138] via-[#0a1a5f] to-[#002f9c]">
      <div
        className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #fbbf24 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
        aria-hidden
      />
      <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-gradient-to-br from-yellow-500/15 to-amber-500/5 rounded-full blur-[90px] translate-x-1/4 -translate-y-1/4 pointer-events-none" aria-hidden />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#0a1a5f]/40 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4 pointer-events-none" aria-hidden />

      <div
        className="absolute top-4 left-4 grid grid-cols-3 gap-1.5 opacity-40 pointer-events-none text-yellow-400"
        aria-hidden
      >
        {[...Array(9)].map((_, i) => (
          <Star key={i} className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400" strokeWidth={0} />
        ))}
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
        <div className="flex-1 text-center lg:text-left space-y-4 mb-6 lg:mb-0">
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-3.5 py-1.5 backdrop-blur-md shadow-inner">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
            </span>
            <Trophy className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
            <span className="text-yellow-400 text-[10px] sm:text-xs font-black tracking-widest uppercase">
              Nieuw Seizoen 2025/26
            </span>
          </div>

          <h1 className="text-3xl sm:text-3xl md:text-4xl lg:text-4xl font-extrabold text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
            Champions League & Eredivisie{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-300 font-black">
              in 4K
            </span>{' '}
            met{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-yellow-200">
              TVdiamonda
            </span>
          </h1>

          <p className="text-white/90 text-sm sm:text-base font-semibold max-w-xl leading-relaxed">
            Elke speelronde van de Champions League en de Eredivisie in HD / 4K, zonder onderbrekingen. Beleef het nieuwe seizoen alsof je in het stadion staat.
          </p>

          <div className="flex flex-wrap justify-center lg:justify-start gap-2.5 pt-2">
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 backdrop-blur-md">
              <Tv className="w-4 h-4 text-yellow-400 shrink-0" />
              <span className="text-white text-xs sm:text-sm font-bold">Live wedstrijden en herhalingen</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 backdrop-blur-md">
              <Monitor className="w-4 h-4 text-yellow-400 shrink-0" />
              <span className="text-white text-xs sm:text-sm font-bold">Alle apparaten</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 backdrop-blur-md">
              <Zap className="w-4 h-4 text-yellow-400 shrink-0" />
              <span className="text-white text-xs sm:text-sm font-bold">Activering in 1 min</span>
            </div>
          </div>

          <div className="pt-4 flex flex-col lg:flex-row flex-wrap items-center justify-center lg:justify-start gap-3">
            {onCtaClick && (
              <button
                type="button"
                onClick={onCtaClick}
                className="w-full lg:w-auto bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 text-slate-950 font-black py-3.5 px-8 rounded-xl shadow-[0_6px_25px_rgba(234,179,8,0.35)] hover:shadow-[0_8px_30px_rgba(234,179,8,0.55)] border border-yellow-400/30 transition-all duration-300 flex items-center justify-center gap-2 text-base transform hover:scale-105 active:scale-95 cursor-pointer"
              >
                <Gift className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
                {ctaLabel}
              </button>
            )}
            <span className="text-white/80 text-xs sm:text-sm font-semibold tracking-wide bg-white/5 border border-white/10 rounded-lg px-3 py-3.5 backdrop-blur-sm w-full sm:w-auto text-center">
              Support 24/7 • Veilig betalen
            </span>
            {showCountdown && <OfferCountdown size="banner" />}
          </div>
        </div>

        <div className="relative w-full lg:w-auto flex justify-center lg:justify-end mt-6 lg:mt-0">
          <div className="relative w-[300px] sm:w-[360px] h-[230px] sm:h-[270px] flex items-center justify-center">
            <div className="absolute w-40 h-40 sm:w-48 sm:h-48 bg-gradient-to-tr from-yellow-500/35 to-amber-500/20 rounded-full blur-2xl animate-pulse pointer-events-none" />

            {/* Left card - Eredivisie logo */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-6 w-[110px] sm:w-[130px] aspect-[3/4] rounded-2xl bg-white shadow-2xl border border-white/20 flex items-center justify-center p-4 z-0">
              <img
                src={eredivisieLogo}
                alt="Eredivisie"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Middle card - Champions League photo */}
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[130px] sm:w-[150px] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-400/40 z-10">
              <img
                src={championsLeaguePhoto}
                alt="Champions League"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent pt-6 pb-2 px-2">
                <p className="text-white text-[9px] font-black text-center uppercase tracking-wide">Champions League</p>
              </div>
              <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md border border-yellow-500/30 rounded-md px-1.5 py-0.5 flex items-center gap-1">
                <span className="flex h-1 w-1 rounded-full bg-yellow-400 animate-pulse" />
                <span className="text-[7px] font-black tracking-widest text-yellow-400 uppercase">4K</span>
              </div>
            </div>

            {/* Right card - Champions League logo */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 rotate-6 w-[110px] sm:w-[130px] aspect-[3/4] rounded-2xl bg-white shadow-2xl border border-white/20 flex items-center justify-center p-4 z-0">
              <img
                src={championsLeagueLogo}
                alt="Champions League"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
